import { api } from "@/api/client";

export interface PickerRow {
  ref: string;
  name: string;
}

export interface OrganizationPickerRow extends PickerRow {
  full_name: string;
  legal_form: string; // "ЮридическоеЛицо" | "ФизическоеЛицо" | ""
}

export async function searchOrganizations(
  q: string,
  connectionId?: string,
): Promise<OrganizationPickerRow[]> {
  const { data } = await api.get<OrganizationPickerRow[]>("/one-c/organizations", {
    params: { q: q || undefined, connection_id: connectionId },
  });
  return data;
}

export async function searchKontragents(
  q: string,
  connectionId?: string,
): Promise<PickerRow[]> {
  const { data } = await api.get<PickerRow[]>("/one-c/kontragents", {
    params: { q: q || undefined, connection_id: connectionId },
  });
  return data;
}

/** Склады и подразделения (`Catalog_СтруктурныеЕдиницы`).
 *  `kind` — «Склад» или «Подразделение»: без него в списке мешаются оба типа
 *  и легко выбрать подразделение там, где нужен склад. */
export async function searchStructuralUnits(
  kind: "Склад" | "Подразделение",
  q: string,
  connectionId?: string,
): Promise<PickerRow[]> {
  const { data } = await api.get<PickerRow[]>("/one-c/structural-units", {
    params: { kind, q: q || undefined, connection_id: connectionId },
  });
  return data;
}

/** Банковские счета организации. `organizationRef` обязателен — счета вне
 *  организации не существует, бэкенд без него отдаст 422. */
export async function searchBankAccounts(
  organizationRef: string,
  q: string,
  connectionId?: string,
): Promise<PickerRow[]> {
  const { data } = await api.get<PickerRow[]>("/one-c/bank-accounts", {
    params: {
      organization_ref: organizationRef,
      q: q || undefined,
      connection_id: connectionId,
    },
  });
  return data;
}

export async function searchDogovors(
  kontragentRef: string,
  q: string,
  options?: {
    connectionId?: string;
    organizationRef?: string;
    // По умолчанию backend сужает до 'СКомиссионером'. Передайте пустую
    // строку чтобы снять фильтр (например, если договор с маркетплейсом
    // заведён как «Прочее»).
    vidDogovora?: string;
  },
): Promise<PickerRow[]> {
  const { data } = await api.get<PickerRow[]>("/one-c/dogovors", {
    params: {
      kontragent_ref: kontragentRef,
      organization_ref: options?.organizationRef,
      vid_dogovora: options?.vidDogovora,
      q: q || undefined,
      connection_id: options?.connectionId,
    },
  });
  return data;
}

/**
 * Элементы справочника технических настроек — хозоперации, состояния, валюта,
 * единица классификатора.
 *
 * `kind` приходит С СЕРВЕРА (в `SystemSettingsView.one_c_refs`), а не
 * перечислен здесь: белый список справочников живёт в одном месте, и вторая
 * его копия во фронте разъехалась бы молча — поле просто перестало бы
 * заполняться.
 */
export async function searchSettingRefs(
  kind: string,
  q: string,
  connectionId?: string,
): Promise<PickerRow[]> {
  const { data } = await api.get<PickerRow[]>(`/one-c/setting-refs/${kind}`, {
    params: { q: q || undefined, connection_id: connectionId },
  });
  return data;
}
